import React, { useContext } from 'react';
import { Box, Chip } from '@mui/material';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import { routerUrls } from 'constants';
import { GlobalContext } from '../context/GlobalState';
import { materialDisplayName } from '../utils/materialLabels';
import { strings } from 'localisation';

// GOTOBED STICKY MATERIAL FILTER BAR
// Single-select filter shown at the top of the products grid. It stays pinned while scrolling
// (mobile + desktop). "All" clears the material (mixed view); each material chip narrows the grid.
// Selecting a chip simply navigates to the products route (with/without :materialId) so the existing
// fetch + pagination logic in Products.jsx reruns for that selection.
export default function MaterialFilterBar({
  materialCategories,
  activeMaterialId,
  categorySlug,
  subCategorySlug,
  categoryId,
  subCategoryId
}) {
  const { lang } = useContext(GlobalContext);
  const allLabel = strings[lang]?.allMaterials || 'Всички';

  // /produkti/{catSlug}/{subSlug}/{catId}/{subCatId}  (append /{materialId} for a specific material)
  const baseUrl =
    routerUrls.products +
    '/' +
    categorySlug +
    '/' +
    subCategorySlug +
    '/' +
    categoryId +
    '/' +
    subCategoryId;

  // Shared pill styling; active = filled gold with a white label, inactive = subtle gold outline.
  // Sizes step down on mobile so more chips fit the row without wrapping/overflow.
  const chipBaseSx = {
    height: { xs: 28, md: 34 },
    borderRadius: '999px',
    fontSize: { xs: '0.75rem', md: '0.875rem' },
    letterSpacing: 0.2,
    cursor: 'pointer',
    transition: 'background-color 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease',
    '& .MuiChip-label': { px: { xs: 1.25, md: 2 } }
  };
  const activeSx = {
    ...chipBaseSx,
    backgroundColor: 'gold.fill',
    // Matches the Load More button: the same deliberate exception to the palette note in
    // theme.js, which reserves gold.fill for ink type because white on it is only 2.77:1.
    color: '#fff',
    fontWeight: 700,
    border: '1px solid',
    borderColor: 'gold.fill',
    '&:hover': { backgroundColor: 'gold.deep', borderColor: 'gold.deep', color: '#fff' }
  };
  const inactiveSx = {
    ...chipBaseSx,
    backgroundColor: 'background.default',
    color: 'text.primary',
    fontWeight: 500,
    border: '1px solid',
    borderColor: 'divider',
    '&:hover': { backgroundColor: 'background.paper', borderColor: 'hairline.strong' }
  };

  const isAllActive = !activeMaterialId;

  return (
    <Box
      sx={{
        position: 'sticky',
        top: 0,
        zIndex: 100,
        backgroundColor: 'background.default',
        borderBottom: '1px solid',
        borderColor: 'divider',
        py: { xs: 0.75, md: 1.25 },
        px: { xs: 1, md: 2 },
        mb: 2,
        display: 'flex',
        alignItems: 'center',
        overflowX: 'auto',
        // hide the scrollbar for a cleaner look (row still scrolls on overflow / touch)
        scrollbarWidth: 'none',
        '&::-webkit-scrollbar': { display: 'none' }
      }}>
      {/* Inner row is centered via auto margins; when it overflows on narrow screens the margins
          collapse to 0 so the full row stays reachable by horizontal scroll (no clipped chips). */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'nowrap',
          gap: { xs: 0.75, md: 1.25 },
          mx: 'auto',
          '& .MuiChip-root': { flexShrink: 0 }
        }}>
        <Chip
          component={Link}
          to={baseUrl}
          clickable
          label={allLabel}
          variant={isAllActive ? 'filled' : 'outlined'}
          sx={isAllActive ? activeSx : inactiveSx}
        />
        {materialCategories &&
          [...materialCategories]
            .reverse()
            .map(
              (material) =>
                material.count > 0 && (
                  <Chip
                    key={material.id}
                    component={Link}
                    to={baseUrl + '/' + material.id}
                    clickable
                    label={`${materialDisplayName(material.name_bg)} (${material.count})`}
                    variant={activeMaterialId == material.id ? 'filled' : 'outlined'}
                    sx={activeMaterialId == material.id ? activeSx : inactiveSx}
                  />
                )
            )}
      </Box>
    </Box>
  );
}

MaterialFilterBar.propTypes = {
  materialCategories: PropTypes.array,
  activeMaterialId: PropTypes.string,
  categorySlug: PropTypes.string,
  subCategorySlug: PropTypes.string,
  categoryId: PropTypes.string,
  subCategoryId: PropTypes.string
};
